/**
 * Race a promise against a timeout so a caller can never hang forever on it.
 *
 * The login flow awaits network requests and PouchDB `destroy()` calls that have
 * no timeout of their own — if one never settles, the Formik submit promise stays
 * pending and the login button is stuck spinning until the tab is reloaded. Wrap
 * those awaits with this so a hang becomes a thrown (and therefore loggable +
 * recoverable) error instead.
 *
 * NOTE: this only unblocks the CALLER — it does not abort the underlying operation
 * (there is no cancel handle for a bare PouchDB `destroy()`). The op keeps running
 * in the background; the caller stops waiting on it.
 *
 * @param {Promise<T>} promise the operation to bound
 * @param {number} ms max time to wait before rejecting
 * @param {string} label short identifier for the wrapped op, e.g. "destroy:loans"
 * @returns {Promise<T>} resolves/rejects with `promise`, or rejects with a
 *   `timeout: <label>` Error (`err.isTimeout === true`, `err.label === label`) on timeout.
 * @template T
 */
export function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((_resolve, reject) => {
    timer = setTimeout(() => {
      const err = new Error(`timeout: ${label} exceeded ${ms}ms`);
      err.isTimeout = true;
      err.label = label;
      reject(err);
    }, ms);
  });

  // Clear the timer once the real promise settles so it never leaks / fires late.
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export default withTimeout;
